import { useToggle } from '@/hooks';
import { Fragment, useMemo } from 'react';
import { Card, CardBody, CardHeader, Col, Collapse, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { BsPatchCheckFill, BsShieldFillCheck } from 'react-icons/bs';
import { FaCheckCircle, FaConciergeBell, FaSwimmingPool, FaVolumeUp } from 'react-icons/fa';
import { FaAngleDown, FaAngleUp, FaSnowflake, FaWifi } from 'react-icons/fa6';
import RoomOptions from './RoomOptions';
import CustomerReview from './CustomerReview';
import HotelPolicies from './HotelPolicies';

const HIGHLIGHT_ICONS = [
  { keyword: ['wifi', 'internet'], icon: FaWifi },
  { keyword: ['pool', 'swim'], icon: FaSwimmingPool },
  { keyword: ['air', 'ac', 'conditioning'], icon: FaSnowflake },
  { keyword: ['service', 'concierge', 'front desk'], icon: FaConciergeBell },
];

const AboutHotel = ({ hotel }) => {
  const hotelId = hotel?.id;
  const { isOpen, toggle } = useToggle();

  const webhotelier = hotel?.webhotelier;
  const descriptionHtml =
    webhotelier?.i18n?.description_html ||
    hotel?.description ||
    hotel?.raw?.description ||
    '';

  const stripHtml = (html) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const descriptionText = stripHtml(descriptionHtml);
  const hasMoreDescription = descriptionText.length > 500;
  const shortDescription = hasMoreDescription ? `${descriptionText.substring(0, 500)}...` : descriptionText;

  const facilityGroups = useMemo(() => {
    const facilities = webhotelier?.facilities || [];
    if (!Array.isArray(facilities) || facilities.length === 0) {
      return [];
    }
    const groups = {};
    facilities.forEach((facility) => {
      const label = facility?.facility_text || facility?.text || facility?.name;
      if (!label) return;
      const category = facility?.category || 'Amenities';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(label);
    });
    return Object.entries(groups).map(([label, items]) => ({ label, items }));
  }, [webhotelier?.facilities]);

  const highlights = useMemo(() => {
    const names = facilityGroups.flatMap((group) => group.items);
    if (names.length === 0) {
      return [
        { icon: FaWifi, label: 'Free Wifi' },
        { icon: FaSwimmingPool, label: 'Swimming Pool' },
        { icon: FaSnowflake, label: 'Central AC' },
        { icon: FaConciergeBell, label: 'Guest Services' },
      ];
    }

    const mapped = [];
    names.forEach((name) => {
      if (mapped.length >= 4) return;
      const lower = name.toLowerCase();
      const match = HIGHLIGHT_ICONS.find((item) => item.keyword.some((key) => lower.includes(key)));
      const Icon = match?.icon || FaCheckCircle;
      mapped.push({ icon: Icon, label: name });
    });
    return mapped.slice(0, 4);
  }, [facilityGroups]);

  return (
    <section className="pt-0">
      <Container data-sticky-container>
        <Row className="g-4 g-xl-5">
          <Col xl={7} className="order-1">
            <div className="vstack gap-5">
              <Card className="bg-transparent">
                <CardHeader className="border-bottom bg-transparent px-0 pt-0">
                  <h3 className="mb-0">About This Hotel</h3>
                </CardHeader>
                <CardBody className="pt-4 p-0">
                  <h5 className="fw-light mb-4">Main Highlights</h5>
                  <div className="hstack gap-3 mb-3">
                    {highlights.map((item, idx) => {
                      const Icon = item.icon;
                      return (
                        <OverlayTrigger key={idx} overlay={<Tooltip>{item.label}</Tooltip>}>
                          <div className="icon-lg bg-light h5 rounded-2 flex-centered">
                            <Icon size={24} />
                          </div>
                        </OverlayTrigger>
                      );
                    })}
                  </div>
                  <p className="mb-3">
                    {shortDescription}
                  </p>
                  <Collapse in={isOpen}>
                    <div>
                      <div className="my-3" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
                    </div>
                  </Collapse>
                  {hasMoreDescription && (
                    <a onClick={toggle} className="p-0 mb-4 mt-2 btn-more d-flex align-items-center collapsed">
                      {!isOpen ? (
                        <Fragment>
                          <span className="see-more" role="button">
                            See more
                          </span>
                          <FaAngleDown className="ms-2" />
                        </Fragment>
                      ) : (
                        <Fragment>
                          <span role="button">See less</span>
                          <FaAngleUp className="ms-2" />
                        </Fragment>
                      )}
                    </a>
                  )}
                  <h5 className="fw-light mb-2">Advantages</h5>
                  <ul className="list-group list-group-borderless mb-0">
                    <li className="list-group-item h6 fw-light d-flex mb-0 items-center">
                      <BsPatchCheckFill className="text-success me-2" />
                      Professional and friendly staff
                    </li>
                    <li className="list-group-item h6 fw-light d-flex mb-0 items-center">
                      <BsPatchCheckFill className="text-success me-2" />
                      Prime location with easy access
                    </li>
                    <li className="list-group-item h6 fw-light d-flex mb-0 items-center">
                      <BsPatchCheckFill className="text-success me-2" />
                      Clean and comfortable accommodations
                    </li>
                    <li className="list-group-item h6 fw-light d-flex mb-0 items-center">
                      <BsPatchCheckFill className="text-success me-2" />
                      Excellent value for money
                    </li>
                  </ul>
                </CardBody>
              </Card>
              <Card className="bg-transparent">
                <CardHeader className="border-bottom bg-transparent px-0 pt-0">
                  <h3 className="card-title mb-0">Amenities</h3>
                </CardHeader>
                <CardBody className="pt-4 p-0">
                  <Row className="g-4">
                    {facilityGroups.length > 0 ? facilityGroups.map((item, idx) => {
                      const Icon = idx === 0 ? FaWifi : idx === 1 ? FaSwimmingPool : idx === 2 ? FaSnowflake : FaConciergeBell;
                      return (
                        <Col sm={6} key={idx}>
                          <h6>
                            <Icon size={18} className="me-2" />
                            {item.label}
                          </h6>
                          <ul className="list-group list-group-borderless mt-2 mb-0">
                            {item.items.map((amenity, amenityIdx) => (
                              <li key={amenityIdx} className="list-group-item pb-0 items-center">
                                <FaCheckCircle className="text-success me-2" />
                                {amenity}
                              </li>
                            ))}
                          </ul>
                        </Col>
                      );
                    }) : (
                      <Col sm={6}>
                        <p className="text-muted mb-0">Amenities will be available soon.</p>
                      </Col>
                    )}
                    <div className="col-sm-6">
                      <h6 className="items-center">
                        <BsShieldFillCheck className="me-2" />
                        Safety & Security
                      </h6>
                      <ul className="list-group list-group-borderless mt-2 mb-4 mb-sm-5">
                        <li className="list-group-item pb-0 items-center">
                          <FaCheckCircle className="text-success me-2" />
                          Secure Parking
                        </li>
                        <li className="list-group-item pb-0 items-center">
                          <FaCheckCircle className="text-success me-2" />
                          24/7 Security
                        </li>
                      </ul>
                      <h6>
                        <FaVolumeUp className="me-2" />
                        Staff Language
                      </h6>
                      <ul className="list-group list-group-borderless mt-2 mb-0">
                        <li className="list-group-item pb-0 items-center">
                          <FaCheckCircle className="text-success me-2" />
                          English
                        </li>
                        <li className="list-group-item pb-0 items-center">
                          <FaCheckCircle className="text-success me-2" />
                          Greek
                        </li>
                      </ul>
                    </div>
                  </Row>
                </CardBody>
              </Card>

              <RoomOptions hotelId={hotelId} hotel={hotel} />

              <CustomerReview />

              <HotelPolicies
                policiesHtml={webhotelier?.i18n?.policies_html}
                generalTermsHtml={webhotelier?.i18n?.general_terms_html}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default AboutHotel;

